import { observer } from "mobx-react";
import _, { add } from "lodash";
import { useStores } from "../../stores/root-store";
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { ShowAlert } from "../../utils/functions";
import { IAddress } from "../../stores/cart";
import { useAuth } from "../../contexts/auth-context";
import ChangePassword from "./change-password";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faUserEdit } from "@fortawesome/free-solid-svg-icons";

const UserProfile = observer(() => {
  const { userStore } = useStores();
  const {
    getUserDetails,
    userDetails,
    getUserAddress,
    userAddress,
    saveUserProfile,
  } = userStore;
  const { isShopAdmin, isShopUser, userId, userName } = useAuth();

  const [isEditView, setIsEditView] = useState<boolean>(false);
  const [userFirstName, setUserFirstName] = useState(userDetails?.firstName);
  const [userLastName, setUserLastName] = useState(userDetails?.surname);
  const [phoneNumber, setPhoneNumber] = useState(userDetails?.phoneNumber);
  const [address, setAddress] = useState<IAddress>({
    fullName: "",
    phoneNumber: "",
    postcode: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
  });
  const [showChangePwdModal, setShowChangePwdModal] = useState<boolean>(false);

  useEffect(() => {
    if (userDetails) {
      setUserFirstName(userDetails.firstName);
      setUserLastName(userDetails.surname);
      setPhoneNumber(userDetails.phoneNumber);
    } else if (userId && !userDetails) getUserDetails(userId);
  }, [userId, userDetails]);

  useEffect(() => {
    if (userAddress) setAddress(userAddress);
    else if (userId && !userAddress) getUserAddress(userId);
  }, [userId, userAddress]);

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleTexAreatChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = evt.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const isFormValid = userId && userFirstName && userLastName ? true : false;
    if (isFormValid) {
      saveUserProfile(
        userId,
        userFirstName,
        userLastName,
        phoneNumber,
        address
      );
    } else {
      ShowAlert.Error("First name, last name fields are required.");
    }
  };

  return (
    <div className="container-fluid mt-3 text-start ps-lg-5 pe-lg-5 mt-3">
      <h4 className="pe-0 ps-0">
        My Account
        {!isEditView && (
          <a
            href="#"
            className="float-end"
            role="button"
            onClick={() => setIsEditView(!isEditView)}
          >
            <FontAwesomeIcon icon={faEdit} className="fs-3 text-default" />
          </a>
        )}
      </h4>
      <form className="card rounded-3 pb-4" autoComplete="off">
        <div className="card-body">
          <div className="panel-body">
            <form autoComplete="off">
              <div className="mb-4">
                <h6>Personal Details</h6>
                <div className="form-floating mb-2">
                  <input
                    type="text"
                    className="form-control"
                    id="email"
                    name="email"
                    placeholder="Email/User Name"
                    disabled
                    value={userName}
                  />
                  <label htmlFor="email">Email/User Name</label>
                </div>
                <div className="row mb-2">
                  <div className="col">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="fnU"
                        placeholder="First name"
                        value={userFirstName}
                        disabled={!isEditView}
                        onChange={(e) => setUserFirstName(e.target.value)}
                      />
                      <label htmlFor="fnU">First Name</label>
                    </div>
                  </div>
                  <div className="col">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="lnU"
                        placeholder="Last name"
                        value={userLastName}
                        disabled={!isEditView}
                        onChange={(e) => setUserLastName(e.target.value)}
                      />
                      <label htmlFor="lnU">Last Name</label>
                    </div>
                  </div>
                </div>
                <div className="form-floating mb-2">
                  <input
                    type="text"
                    className="form-control"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={phoneNumber}
                    disabled={!isEditView}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  <label htmlFor="phoneNumber">Phone Number</label>
                </div>
                <div>
                  <a
                    href="#"
                    onClick={() => setShowChangePwdModal(!showChangePwdModal)}
                  >
                    Change Password
                  </a>
                </div>
              </div>

              {!isShopAdmin && !isShopUser && (
                <div>
                  <h6>Saved Address</h6>
                  <div className="form-floating mb-2">
                    <input
                      type="text"
                      className="form-control"
                      id="fnU"
                      name="fullName"
                      placeholder="Full Name"
                      required
                      value={address.fullName}
                      disabled={!isEditView}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="fnU">Full name</label>
                  </div>
                  <div className="row mb-2">
                    <div className="col">
                      <div className="form-floating">
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          name="phoneNumber"
                          placeholder="Phone No."
                          value={address.phoneNumber}
                          disabled={!isEditView}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="phone">Phone No.</label>
                      </div>
                    </div>
                    <div className="col">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="postcode"
                          placeholder="Post Code"
                          name="postcode"
                          value={address.postcode}
                          disabled={!isEditView}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="postcode">Post Code</label>
                      </div>
                    </div>
                  </div>
                  <div className="form-floating mb-2">
                    <textarea
                      className="form-control"
                      id="addressLine1"
                      placeholder="Address Line 1"
                      name="addressLine1"
                      rows={4}
                      value={address.addressLine1}
                      disabled={!isEditView}
                      onChange={handleTexAreatChange}
                    />
                    <label htmlFor="addressLine1">Address Line 1</label>
                  </div>
                  <div className="form-floating mb-2">
                    <textarea
                      className="form-control"
                      id="addressLine2"
                      placeholder="Address Line 2"
                      name="addressLine2"
                      rows={4}
                      value={address.addressLine2}
                      disabled={!isEditView}
                      onChange={handleTexAreatChange}
                    />
                    <label htmlFor="addressLine3">
                      Address Line 2 (Optional)
                    </label>
                  </div>
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      id="city"
                      placeholder="City"
                      name="city"
                      value={address.city}
                      disabled={!isEditView}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="city">City</label>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
        {isEditView && (
          <div className="d-flex">
            <button
              className="btn btn-secondary flex-fill me-2"
              onClick={() => setIsEditView(false)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="btn btn-default flex-fill"
              onClick={handleFormSubmit}
              type="button"
            >
              <i className="fa fa-check me-1"></i>
              Save
            </button>
          </div>
        )}
      </form>

      {showChangePwdModal && (
        <ChangePassword
          show={showChangePwdModal}
          setShowModal={(show: boolean) => setShowChangePwdModal(show)}
        />
      )}
    </div>
  );
});

export default UserProfile;
